import React from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'

import PrivateRoute from 'app/components/PrivateRoute'
import {
  navigationMenus,
  publicNavigationPaths,
  additionalNavigationPaths,
  mainLayoutNavigationPaths
} from 'app/config'
import { useAccess } from 'app/hooks/useAccess'
import MainLayout from 'app/layouts/MainLayout'
import { firstAllowedPath, idFor } from 'app/permissions'
import RoutePages from 'app/routes/RoutePages'
import { WebSocketProvider } from 'app/WebSocketProvider'

// Phase-2 URL normalization (2026-05-13): old URLs redirect to the new clean
// scheme. Bookmarks + external links continue to work.
const LEGACY_REDIRECTS = [
  // Observations promoted out of Preschool Planner to a top-level HQ tab
  ['/programs/planner/plans-observations', '/observations'],
  // Home (was /dashboard/*)
  ['/dashboard/parent', '/home/parent'],
  ['/dashboard/teacher', '/home/teacher'],
  // People (was /user/*)
  ['/user/student', '/people/student'],
  ['/user/parent', '/people/parent'],
  ['/user/teacher', '/people/teacher'],
  ['/user/admin', '/people/admin'],
  // Classes (was /class/* + /attendance)
  ['/class/batch', '/classes/batch'],
  ['/class/summercampbatch', '/classes/summer-camp'],
  ['/class/enrolment', '/classes/enrolment'],
  ['/class/excelenrolment', '/classes/excel-enrolment'],
  ['/class/psacenrolment', '/classes/school-care-enrolment'],
  ['/class/toddlerclubenrolment', '/classes/toddler-club-enrolment'],
  ['/class/exploreenrolment', '/classes/explore-enrolment'],
  ['/class/trialsessions', '/classes/trial-sessions'],
  ['/class/toddlerclubsession', '/classes/toddler-club-sessions'],
  ['/class/exploresession', '/classes/explore-sessions'],
  ['/class/makeupsession/showmakeupsession', '/classes/makeup'],
  ['/class/makeupsession/bookmakeupsession', '/classes/makeup/book'],
  ['/attendance', '/classes/attendance/mark'],
  // Programs (was /class/* + /journey/* + /portfolio + /community/club)
  ['/class/subject-category', '/programs/subject-categories'],
  ['/class/subject', '/programs/subjects'],
  ['/class/playdate-subject', '/programs/subjects/playdate'],
  ['/class/toddlerclub-subject', '/programs/subjects/toddler-club'],
  ['/class/standard', '/programs/standards'],
  ['/class/course', '/programs/courses'],
  // Operations (was /class/inventory, /inventory/*, /centre, /scheduler/*)
  ['/class/inventory', '/operations/inventory'],
  ['/inventory/orders', '/operations/orders'],
  ['/inventory/instalmentorders', '/operations/orders/instalment'],
  ['/inventory/dropincareorders', '/operations/orders/drop-in-care'],
  ['/centre', '/operations/centres'],
  ['/visitor', '/operations/visitor-entry'],
  // Support
  ['/communication/teacher-parent-chat', '/support/chat/teacher-parent'],
  ['/communication/centre-parent-chat', '/support/chat/centre-parent'],
  ['/communication/ticket', '/support/tickets'],
  ['/communication/bulk-messaging', '/support/bulk-messaging'],
  ['/communication/announcements', '/support/announcements'],
  ['/community/classroom-moments', '/support/moments'],
  ['/communication/interactionhistory', '/support/interaction-history'],
  ['/community/openbook', '/support/openbook'],
  ['/request', '/support/demo-scheduling']
]

// Role-specific landing pages. A teacher whose day is basically one screen
// should land there, not on firstAllowedPath's nav-order guess (which, for a
// Preschool Teacher holding several tabs, is whatever comes first in the tree,
// not the planner). Keyed by Role.name as returned by /base/me
// (access.role.name), so these strings MUST match the Role records in the DB.
// Each target sits inside that role's own permissions, so PrivateRoute won't
// bounce it. Roles not listed here fall through to firstAllowedPath unchanged.
const ROLE_LANDING = {
  'Preschool Teacher': mainLayoutNavigationPaths.lessonPlanner, // /teacher/lesson-planner
  'Afterschool Teacher': mainLayoutNavigationPaths.analyticsTeacher // /support/analytics/teacher
}

const roleLandingPath = (access) => ROLE_LANDING[access?.role?.name] || null

const RouteHandler = () => {
  // parentId is threaded so each route carries the SAME permission id the
  // catalogue and the sidebar use (idFor is the one place ids are built) —
  // otherwise a role could grant a page the router then refuses.
  const buildMainRoutes = React.useCallback((menu, parentId = '') => {
    return menu.map((item) => {
      const id = idFor(item, parentId)

      if (item.subMenus) {
        return buildMainRoutes(item.subMenus, id)
      } else if (item.path) {
        return (
          <PrivateRoute
            exact
            key={item.path}
            path={item.path}
            permissionId={id}
            component={RoutePages[item.component || null]}
          />
        )
      }

      return null
    })
  }, [])

  const mainRoutes = React.useMemo(() => {
    return buildMainRoutes(navigationMenus)
  }, [buildMainRoutes])

  // "/" lands on the parent dashboard, which a restricted role may not hold —
  // they'd log in straight onto a "no access" screen. Send them to the first
  // page their role actually allows. (Before /base/me answers, access is null
  // and firstAllowedPath returns the default, which is correct for the common
  // unrestricted case and harmless otherwise: PrivateRoute re-checks.)
  const { access } = useAccess()
  const landingPath = React.useMemo(
    () =>
      roleLandingPath(access) ||
      firstAllowedPath(access) ||
      mainLayoutNavigationPaths.parentdashboard,
    [access]
  )

  const [
    publicNavigationPathsArray,
    mainLayoutNavigationPathsArray,
    additionalNavigationPathsArray
  ] = React.useMemo(() => {
    return [
      Object.values(publicNavigationPaths),
      Object.values(mainLayoutNavigationPaths),
      Object.values(additionalNavigationPaths)
    ]
  }, [])

  return (
    <Switch>
      {LEGACY_REDIRECTS.map(([from, to]) => (
        // `to` must be a location object, not a string: a string <Redirect>
        // replaces the whole location and DROPS location.search, so every legacy
        // deep link that carries state in the query string lost it — e.g.
        // /dashboard/parent?parent=<uuid> (generated by history.js and by
        // external bookmarks) landed on /home/parent with no uuid, and
        // ParentDashboard reads that uuid from window.location.search.
        <Route
          key={from}
          exact
          path={from}
          render={({ location }) => (
            <Redirect
              to={{
                pathname: to,
                search: location.search,
                hash: location.hash
              }}
            />
          )}
        />
      ))}

      <Route exact path={publicNavigationPathsArray}>
        <Route exact path="/">
          <Redirect to={landingPath} />
        </Route>

        <React.Suspense fallback={null}>
          <Route
            exact
            path={publicNavigationPaths.login}
            component={RoutePages['LoginPage']}
          />
        </React.Suspense>
        <React.Suspense fallback={null}>
          <Route
            exact
            path={publicNavigationPaths.schoolAttendance}
            component={RoutePages['SchoolAttendancePage']}
          />
        </React.Suspense>
        <React.Suspense fallback={null}>
          {/* The parent's underage-consent page. No login: the token in the path
              is the authorisation, and a parent must not need an Alfred account
              to give consent for their own child. */}
          <Route
            exact
            path={publicNavigationPaths.underageConsent}
            component={RoutePages['UnderageConsentPublicPage']}
          />
        </React.Suspense>
        <React.Suspense fallback={null}>
          {/* The new joiner's signed-offer upload (HQ -> New Joiners). No
              login: the token in the path is the authorisation. */}
          <Route
            exact
            path={[
              publicNavigationPaths.newJoinerUpload,
              publicNavigationPaths.newJoinerUploadShort
            ]}
            component={RoutePages['NewJoinerUploadPublicPage']}
          />
        </React.Suspense>
        <React.Suspense fallback={null}>
          {/* The new joiner's joining-details form (Keka + ID card). No
              login: the token in the path is the authorisation. */}
          <Route
            exact
            path={[
              publicNavigationPaths.joiningDetails,
              publicNavigationPaths.joiningDetailsShort
            ]}
            component={RoutePages['JoiningDetailsPublicPage']}
          />
        </React.Suspense>
        <React.Suspense fallback={null}>
          {/* The centre's issue form. The token in the path is the centre.
              It lives outside MainLayout (no sidebar, phone-first) but the
              page itself redirects to login when there is no session — the
              raiser's name comes from the Alfred account (2026-09-22). */}
          <Route
            exact
            path={publicNavigationPaths.reportIssue}
            component={RoutePages['ReportIssuePublicPage']}
          />
        </React.Suspense>
        <React.Suspense fallback={null}>
          {/* New Centre Audit / Housekeeping Audit. Same shape as the issue
              form: no sidebar, centre from the link, login needed. */}
          <Route
            exact
            path={publicNavigationPaths.auditForm}
            component={RoutePages['AuditFormPublicPage']}
          />
        </React.Suspense>
        <React.Suspense fallback={null}>
          {/* The parent-filled alternative to Visitor Entry. No login: a
              parent scans the centre's QR and fills it on their own phone. */}
          <Route
            exact
            path={publicNavigationPaths.newParentForm}
            component={RoutePages['NewParentFormPublicPage']}
          />
        </React.Suspense>
      </Route>

      <Route exact path={mainLayoutNavigationPathsArray}>
        <WebSocketProvider>
          <MainLayout>
            <Switch>
              <React.Suspense fallback={null}>{mainRoutes}</React.Suspense>
            </Switch>
          </MainLayout>
        </WebSocketProvider>
      </Route>

      <Route exact path={additionalNavigationPathsArray}>
        <React.Suspense fallback={null}>
          <PrivateRoute
            exact
            path={additionalNavigationPaths.demoScheduling}
            component={RoutePages['DemoSchedulingPage']}
          />

          <PrivateRoute
            exact
            path={additionalNavigationPaths.visitorEntry}
            component={RoutePages['VisitorEntryPage']}
          />
        </React.Suspense>
      </Route>

      <Route exact path={publicNavigationPaths.notFound} component={null} />
    </Switch>
  )
}

export default RouteHandler
